import axios from 'axios';

// Base URL — your Spring Boot app
//const BASE_URL = 'http://localhost:8080';
const BASE_URL = process.env.REACT_APP_API_URL 
  || 'http://localhost:8080';

const api = axios.create({
  baseURL: BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor — automatically adds JWT token to every request
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('edunex_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Interceptor — handles 401 (token expired) globally
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('edunex_token');
      localStorage.removeItem('edunex_user');
      // Show toast before redirecting
      const event = new CustomEvent('auth:expired');
      window.dispatchEvent(event);
      setTimeout(() => {
        window.location.href = '/login';
      }, 1500);
    }
    return Promise.reject(error);
  }
);

// ── Auth ──────────────────────────────────────────────────
export const authApi = {
  login: (email: string, password: string) =>
    api.post('/api/v1/auth/login', { email, password }),

  register: (data: {
    email: string;
    password: string;
    fullName: string;
    tenantSlug: string;
  }) => api.post('/api/v1/auth/register', data),

  me: () => api.get('/api/v1/auth/me'),
};

// ── Courses ───────────────────────────────────────────────
export const courseApi = {
  getAll: (page = 0, size = 10) =>
    api.get(`/api/v1/courses?page=${page}&size=${size}`),

  getOne: (id: string) =>
    api.get(`/api/v1/courses/${id}`),

  enroll: (courseId: string) =>
    api.post('/api/v1/courses/enroll', { courseId }),

  myEnrollments: () =>
    api.get('/api/v1/courses/my-enrollments'),
};

// ── Modules & Lessons ─────────────────────────────────────
export const lessonApi = {
  getModules: (courseId: string) =>
    api.get(`/api/v1/courses/${courseId}/modules`),

  getLessons: (moduleId: string) =>
    api.get(`/api/v1/modules/${moduleId}/lessons`),

  getLesson: (lessonId: string) =>
    api.get(`/api/v1/lessons/${lessonId}`),

  complete: (lessonId: string) =>
    api.post(`/api/v1/lessons/${lessonId}/complete`),

  // Add to lessonApi in client.ts:
updateContent: (lessonId: string, data: {
  contentBlocks: any[];
  title?: string;
}) => api.put(`/api/v1/lessons/${lessonId}/content`, data),
};

// ── AI Study Buddy ────────────────────────────────────────
export const aiApi = {
  ask: (question: string, lessonId?: string, sessionId?: string) =>
    api.post('/api/v1/ai/ask', { question, lessonId, sessionId }),
};

// ── Assessments ───────────────────────────────────────────
export const assessmentApi = {
  getByCourse: (courseId: string) =>
    api.get(`/api/v1/assessments?courseId=${courseId}`),

  getOne: (assessmentId: string) =>
    api.get(`/api/v1/assessments/${assessmentId}`),

  getQuestions: (assessmentId: string) =>
    api.get(`/api/v1/assessments/${assessmentId}/questions`),

generateQuestions: (data: {
  lessonId: string;
  questionCount: number;
  difficulty: string;
  questionType: string;
}) => api.post('/api/v1/ai/generate-questions', data),

  submit: (
    assessmentId: string,
    answers: Array<{ questionId: string; studentAnswer: string }>,
    timeTakenSecs: number
  ) =>
    api.post('/api/v1/assessments/submit', {
      assessmentId,
      answers,
      timeTakenSecs,
    }),
};

export const flashcardApi = {
  generate: (lessonId: string) =>
    api.post(`/api/v1/flashcards/generate/${lessonId}`),

  getDueToday: () =>
    api.get('/api/v1/flashcards/due-today'),

  getDueCount: () =>
    api.get('/api/v1/flashcards/due-count'),

  submitReview: (reviewId: string, difficulty: string) =>
    api.post(`/api/v1/flashcards/review/${reviewId}?difficulty=${difficulty}`),
};

export default api;